import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import settingsService from "../services/settingsService";
import styles from "../assets/settings.module.css";

const SettingsEmail = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email_about_dismissed_guardian: "",
    email_about_initial_inspection_report: "",
  });
  const [loading, setLoading] = useState(false);

  const loadEmailSettings = useCallback(async () => {
    try {
      setLoading(true);
      const response = await settingsService.getEmailSettings();

      if (response.success) {
        setFormData(response.data);
      } else if (response.message === "You don't have permissions to view email settings!") {
        // Handle access control error
        toast.error("Access denied: You don't have permission to view email settings");
        // Redirect to dashboard after 2 seconds
        setTimeout(() => navigate("/dashboard"), 2000);
      } else {
        throw new Error(response.message || "Failed to load email settings");
      }
    } catch (error) {
      console.error("Failed to load email settings:", error);
      
      // Check if it's an access control error
      if (error.response && error.response.status === 403) {
        toast.error("Access denied: You don't have permission to view email settings");
        // Redirect to dashboard after 2 seconds
        setTimeout(() => navigate("/dashboard"), 2000);
        return;
      }

      // Fallback to mock data
      setFormData(settingsService.getMockEmailSettingsData());
      toast.warning("Using mock data - backend unavailable");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    loadEmailSettings();
  }, [loadEmailSettings]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await settingsService.updateEmailSettings(formData);

      if (response.success) {
        toast.success("Email settings updated successfully!");
      } else {
        // Handle validation errors
        if (response.errors) {
          // Display each validation error as a separate toast
          Object.values(response.errors).forEach(error => {
            toast.error(error);
          });
        } else if (response.message === "You don't have permissions to update email settings!") {
          // Handle access control error
          toast.error("Access denied: You don't have permission to update email settings");
          // Redirect to dashboard after 2 seconds
          setTimeout(() => navigate("/dashboard"), 2000);
        } else {
          throw new Error(response.message || "Failed to update email settings");
        }
      }
    } catch (error) {
      console.error("Failed to update email settings:", error);
      
      // Check if it's an axios error with a response
      if (error.response) {
        const { data } = error.response;
        
        // Handle validation errors
        if (data && data.errors) {
          Object.values(data.errors).forEach(error => {
            toast.error(error);
          });
        } 
        // Handle access control error
        else if (data && data.message && data.message.includes("permissions")) {
          toast.error("Access denied: You don't have permission to update email settings");
          // Redirect to dashboard after 2 seconds
          setTimeout(() => navigate("/dashboard"), 2000);
        } else {
          toast.error(data?.message || "Failed to update email settings");
        }
      } else {
        toast.error(error.message || "Failed to update email settings");
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading && !formData.email_about_dismissed_guardian) {
    return (
      <div className={styles.container}>
        <div className={styles.row}>
          <div className={styles.colMd12}>
            <div
              className={
                styles.panel +
                " " +
                styles.panelDefault +
                " " +
                styles.panelStandard
              }
            >
              <div className={styles.panelBody + " " + styles.textCenter}>
                <div className={styles.spinnerBorder} role="status">
                  <span className={styles.srOnly}>Loading...</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Breadcrumb */}
      <div className="breadcrumb-row">
        <span
          className="breadcrumb-main"
          style={{
            fontWeight: 400,
            fontSize: "14px",
            color: "black",
            cursor: "pointer",
          }}
          onClick={() => navigate("/dashboard")}
        >
          Dashboard
        </span>
        <div
          style={{ background: "#30C693", fontWeight: 400, fontSize: "14px" }}
        >
          <span className="breadcrumb-sep">/</span>
          <span className="breadcrumb-properties">Menu</span>

          <span className="breadcrumb-sep">/</span>
          <span className="breadcrumb-properties">Email Settings</span>
        </div>
      </div>

      <div className={styles.row}>
        <div className={styles.colMd12}>
          <div
            className={
              styles.panel +
              " " +
              styles.panelDefault +
              " " +
              styles.panelStandard
            }
          >
            <div
              className={styles.panelHeading + " " + styles.panelTitle}
              style={{ backgroundColor: "transparent" }}
            >
              <h3>Email Settings</h3>
            </div>
            <div className={styles.panelBody}>
              <form
                onSubmit={handleSubmit}
                id="form_settings"
                encType="multipart/form-data"
              >
                <fieldset
                  className={styles.formHorizontal + " " + styles.formBackend}
                >
                  <div className={styles.formGroup}>
                    <label
                      className={
                        styles.controlLabel +
                        " " +
                        styles.colSm3 +
                        " " +
                        styles.labelRequired
                      }
                    >
                      Email about dismissed guardian
                    </label>
                    <div className={styles.colSm9}>
                      <input
                        type="email"
                        name="email_about_dismissed_guardian"
                        value={formData.email_about_dismissed_guardian}
                        onChange={handleInputChange}
                        className={styles.formControl}
                        required
                      />
                      <span className={styles.helpBlock}>
                        Enter email address to receive notifications about
                        dismissed guardians.
                      </span>
                    </div>
                  </div>

                  <input
                    type="hidden"
                    name="email_about_initial_inspection_report"
                    value={formData.email_about_initial_inspection_report}
                  />

                  <div className={styles.clearfix}></div>
                </fieldset>

                <div className={styles.formGroup + " " + styles.textRight}>
                  <button
                    type="submit"
                    className={
                      styles.btn +
                      " " +
                      styles.btnHead +
                      " " +
                      styles.btnStandard
                    }
                    disabled={loading}
                  >
                    <i
                      className={styles.glyphicon + " " + styles.glyphiconOk}
                    ></i>
                    {loading ? " Updating..." : " Update"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsEmail;
